import React, { useRef } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Transition } from 'react-transition-group';
import styles from './Collapse.module.scss';

const Collapse = (props) => {
  const {
    children,
    className,
    in: inProp,
    onEnter,
    onEntered,
    onEntering,
    onExit,
    onExited,
    onExiting,
    timeout = 250,
    style,
  } = props;

  const wrapperRef = useRef(null);
  const nodeRef = useRef(null);

  const normalizedTransitionCallback = (callback) => (maybeIsAppearing) => {
    if (callback) {
      const node = nodeRef.current;

      if (maybeIsAppearing === undefined) {
        callback(node);
      } else {
        callback(node, maybeIsAppearing);
      }
    }
  };

  const getWrapperSize = () => (wrapperRef.current ? wrapperRef.current.clientHeight : 0);

  const handleEnter = normalizedTransitionCallback((node, isAppearing) => {
    node.style.height = 0;

    if (onEnter) {
      onEnter(node, isAppearing);
    }
  });

  const handleEntering = normalizedTransitionCallback((node, isAppearing) => {
    node.style.transitionDuration = `${timeout}ms`;
    node.style.height = `${getWrapperSize()}px`;

    if (onEntering) {
      onEntering(node, isAppearing);
    }
  });

  const handleEntered = normalizedTransitionCallback((node, isAppearing) => {
    node.style.height = 'auto';

    if (onEntered) {
      onEntering(node, isAppearing);
    }
  });

  const handleExit = normalizedTransitionCallback((node) => {
    node.style.height = `${getWrapperSize()}px`;

    if (onExit) {
      onExit(node);
    }
  });

  const handleExited = normalizedTransitionCallback(onExited);

  const handleExiting = normalizedTransitionCallback((node) => {
    node.style.transitionDuration = `${timeout}ms`;
    node.style.height = `${getWrapperSize()}px`;
    node.style.height = 0;

    if (onExiting) {
      onExiting(node);
    }
  });

  return (
    <Transition
      in={inProp}
      onEnter={handleEnter}
      onEntered={handleEntered}
      onEntering={handleEntering}
      onExit={handleExit}
      onExited={handleExited}
      onExiting={handleExiting}
      timeout={timeout}
    >
      {(state) => (
        <div
          style={style}
          className={classNames(
            styles.root,
            {
              [styles.entered]: state === 'entered',
            },
            className
          )}
          ref={nodeRef}
        >
          <div className={styles.wrapper} ref={wrapperRef}>
            <div className={styles.wrapperInner}>{children}</div>
          </div>
        </div>
      )}
    </Transition>
  );
};

Collapse.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  in: PropTypes.bool,
  onEnter: PropTypes.func,
  onEntered: PropTypes.func,
  onEntering: PropTypes.func,
  onExit: PropTypes.func,
  onExited: PropTypes.func,
  onExiting: PropTypes.func,
  timeout: PropTypes.number,
  style: PropTypes.object
};

export { Collapse };
